import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import useIsMobile from "@/lib/useIsMobile";
import useFollowing from "@/lib/useFollowing";
import { Loader2, Sparkles, Trophy, TrendingUp, CalendarDays, ArrowUpRight, Swords, Compass, Flame, Plus, Check, Search, Users, X, Microscope, Bookmark, Trash2, ExternalLink, RefreshCw } from "lucide-react";

const money = (n) => "$" + Math.round(n || 0).toLocaleString();
const RANGES = [{ d: 7, label: "7 days" }, { d: 14, label: "2 weeks" }, { d: 30, label: "30 days" }, { d: 45, label: "45 days" }];

export default function WeeklyDiscovery() {
  const m = useIsMobile();
  const nav = useNavigate();
  const { following: myFollowing, isFollowing, follow } = useFollowing();
  const [days, setDays] = useState(14);
  const [recap, setRecap] = useState(null);
  const [hi, setHi] = useState(null);
  const [movers, setMovers] = useState(null);
  const [h2h, setH2h] = useState(null);
  const [rec, setRec] = useState(null);
  const [hot, setHot] = useState(null);
  const [riv, setRiv] = useState(null);
  const [totals, setTotals] = useState({});

  // deep research recap + flagged runs dataset
  const [deep, setDeep] = useState(undefined); // undefined=loading, null=none
  const [deepLoading, setDeepLoading] = useState(false);
  const [deepErr, setDeepErr] = useState(null);
  const [flagged, setFlagged] = useState([]);
  const [roster, setRoster] = useState([]);
  const [rosterInput, setRosterInput] = useState("");

  // search-to-follow
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const tRef = useRef(null);

  useEffect(() => {
    setRecap(null); setHi(null); setH2h(null); setRec(null); setHot(null);
    api.get(`/weekly/recap?days=${days}`).then((r) => setRecap(r.data)).catch(() => setRecap({ riders: [], no_results: true }));
    api.get(`/weekly/highlights?days=${days}`).then((r) => setHi(r.data)).catch(() => setHi({ events: {}, empty: true }));
    api.get(`/weekly/head-to-head?days=${days}`).then((r) => setH2h(r.data)).catch(() => setH2h({ matchups: [], empty: true }));
    api.get(`/weekly/recommended?days=${days}`).then((r) => setRec(r.data)).catch(() => setRec({ groups: [], empty: true }));
    api.get(`/weekly/rising-stars?days=${days}`).then((r) => setHot(r.data)).catch(() => setHot({ rising: [], empty: true }));
  }, [days]);
  useEffect(() => {
    api.get(`/standings/movers`).then((r) => setMovers(r.data)).catch(() => setMovers({ ready: false, climbers: [] }));
  }, []);
  const loadTotals = () => api.get(`/competitors/following-totals`).then((r) => setTotals(r.data?.totals || {})).catch(() => {});
  useEffect(() => { loadTotals(); }, []);
  useEffect(() => { loadTotals(); }, [myFollowing.length]);
  const loadRivalries = () => api.get(`/weekly/rivalries`).then((r) => setRiv(r.data)).catch(() => setRiv({ rivalries: [], empty: true }));
  useEffect(() => { loadRivalries(); }, []);
  useEffect(() => { setRiv(null); loadRivalries(); }, [myFollowing.length]);

  const loadFlagged = () => api.get(`/weekly/flagged-runs`).then((r) => setFlagged(r.data?.runs || [])).catch(() => {});
  useEffect(() => {
    api.get(`/weekly/deep-recap`).then((r) => setDeep(r.data?.exists ? r.data : null)).catch(() => setDeep(null));
    api.get(`/weekly/research-roster`).then((r) => setRoster(r.data?.names || [])).catch(() => {});
    loadFlagged();
  }, []);

  const saveRoster = (names) => { setRoster(names); api.post(`/weekly/research-roster`, { names }).then((r) => setRoster(r.data?.names || names)).catch(() => {}); };
  const addRosterName = () => {
    const v = rosterInput.trim();
    if (!v) return;
    if (!roster.some((n) => n.toLowerCase() === v.toLowerCase())) saveRoster([...roster, v]);
    setRosterInput("");
  };
  const removeRosterName = (name) => saveRoster(roster.filter((n) => n !== name));

  const generateDeep = () => {
    setDeepLoading(true); setDeepErr(null);
    api.post(`/weekly/deep-recap?days=${days}`).then((r) => { setDeep(r.data); loadFlagged(); })
      .catch((e) => setDeepErr(e?.response?.data?.detail || "Research failed. Please try again."))
      .finally(() => setDeepLoading(false));
  };
  const deleteFlagged = (id) => { setFlagged((f) => f.filter((x) => x.id !== id)); api.delete(`/weekly/flagged-runs/${id}`).catch(() => {}); };

  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);
    if (q.trim().length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    tRef.current = setTimeout(() => {
      api.get(`/competitors/search?q=${encodeURIComponent(q.trim())}`).then((r) => setResults(r.data || [])).catch(() => setResults([])).finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(tRef.current);
  }, [q]);

  const bg = m ? "bg-[#0E1116] text-slate-100" : "bg-[#F6F7F9] text-[#14181F]";
  const card = m ? "bg-[#161B22] border border-white/5" : "bg-white border border-[#E4E7EB]";
  const inner = m ? "bg-white/5" : "bg-[#F6F7F9]";
  const sub = m ? "text-slate-400" : "text-slate-500";
  const inputCls = m ? "bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500" : "bg-white border-[#E4E7EB] text-[#14181F] placeholder:text-slate-400";

  const FollowBtn = ({ name }) => {
    const on = isFollowing(name);
    return (
      <button
        data-testid={`weekly-follow-${name}`}
        onClick={(e) => { e.stopPropagation(); if (!on) follow(name); }}
        disabled={on}
        className={`shrink-0 text-[11px] font-bold rounded-full px-2.5 py-1 flex items-center gap-1 transition-colors ${on ? (m ? "bg-white/10 text-slate-400" : "bg-[#ECEEF1] text-slate-500") : "bg-[#C8000F] text-white hover:bg-[#a4000c]"}`}>
        {on ? <><Check className="w-3 h-3" /> Following</> : <><Plus className="w-3 h-3" /> Follow</>}
      </button>
    );
  };

  const CfrBadge = () => <span className="text-[9px] font-bold uppercase bg-[#E0A800]/20 text-[#B07f00] rounded px-1.5 py-0.5 ml-1">CFR</span>;

  const mdComponents = {
    a: ({ node, ...p }) => <a {...p} target="_blank" rel="noreferrer" className="text-[#C8000F] underline underline-offset-2 hover:opacity-80" />,
    h1: ({ node, ...p }) => <h3 {...p} className="font-display font-bold text-base mt-4 mb-1.5" />,
    h2: ({ node, ...p }) => <h3 {...p} className="font-display font-bold text-base mt-4 mb-1.5" />,
    h3: ({ node, ...p }) => <h4 {...p} className="font-bold text-[14px] mt-3 mb-1" />,
    p: ({ node, ...p }) => <p {...p} className="text-[14px] leading-relaxed mb-2" />,
    ul: ({ node, ...p }) => <ul {...p} className="list-disc pl-5 space-y-1 mb-2 text-[13.5px]" />,
    ol: ({ node, ...p }) => <ol {...p} className="list-decimal pl-5 space-y-1 mb-2 text-[13.5px]" />,
    li: ({ node, ...p }) => <li {...p} className="leading-relaxed" />,
    strong: ({ node, ...p }) => <strong {...p} className="font-bold" />,
    hr: () => <hr className={`my-3 ${m ? "border-white/10" : "border-[#E4E7EB]"}`} />,
  };

  const followedNames = Object.keys(totals);

  return (
    <div className={`min-h-screen ${bg} px-4 lg:px-8 py-6 pb-24`} data-testid="weekly-discovery">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <h1 className="font-display text-2xl lg:text-3xl font-extrabold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#C8000F]" /> Weekly Discovery
          </h1>
          <div className="flex gap-1.5">
            {RANGES.map((r) => (
              <button key={r.d} onClick={() => setDays(r.d)} data-testid={`weekly-range-${r.d}`}
                className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors ${days === r.d ? "bg-[#C8000F] text-white" : (m ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#ECEEF1] text-slate-600 hover:bg-[#E0E3E7]")}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <p className={`text-sm ${sub} mb-5`}>Search for anyone to follow, see how your riders did this week (season + CFR), and discover the names worth tracking.</p>

        {/* SEARCH TO FOLLOW */}
        <section className={`rounded-2xl p-4 lg:p-5 mb-5 ${card}`} data-testid="weekly-search">
          <div className="flex items-center gap-2 mb-3"><Search className="w-4 h-4 text-[#C8000F]" /><h2 className="font-display font-bold text-lg">Find a rider to follow</h2></div>
          <div className="relative">
            <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${sub}`} />
            <input
              data-testid="weekly-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name — e.g. Karli Cowie, Zeke Thurston…"
              className={`w-full rounded-xl border pl-9 pr-9 py-2.5 text-sm outline-none focus:border-[#C8000F] transition-colors ${inputCls}`}
            />
            {q && <button onClick={() => setQ("")} data-testid="weekly-search-clear" className={`absolute right-3 top-1/2 -translate-y-1/2 ${sub} hover:text-[#C8000F]`}><X className="w-4 h-4" /></button>}
          </div>
          {q.trim().length >= 2 && (
            <div className="mt-3 space-y-1.5" data-testid="weekly-search-results">
              {searching ? <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-[#C8000F]" /></div>
                : (results || []).length === 0 ? <p className={`text-sm ${sub} py-1`}>No riders match "{q}". Try a different spelling.</p>
                  : (results || []).map((r, k) => (
                    <div key={k} className={`rounded-xl p-2.5 flex items-center justify-between gap-3 ${inner}`} data-testid={`weekly-search-${k}`}>
                      <div className="min-w-0">
                        <div className="font-bold text-[14px] truncate">{r.name}</div>
                        {r.hometown && <div className={`text-[12px] ${sub} truncate`}>{r.hometown}</div>}
                      </div>
                      <FollowBtn name={r.name} />
                    </div>
                  ))}
            </div>
          )}
        </section>

        {/* RIDERS YOU FOLLOW */}
        <section className={`rounded-2xl p-4 lg:p-5 mb-5 ${card}`} data-testid="weekly-your-riders">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#C8000F]" /><h2 className="font-display font-bold text-lg">Riders You Follow</h2></div>
            <button onClick={() => nav("/riders")} className="text-xs font-semibold text-[#C8000F] hover:underline" data-testid="weekly-open-myriders">Open My Riders →</button>
          </div>
          {followedNames.length === 0 ? <p className={`text-sm ${sub}`}>You're not following anyone yet — use the search above or the recommendations below to start tracking riders.</p>
            : <div className="grid sm:grid-cols-2 gap-2">
              {followedNames.map((name) => {
                const t = totals[name] || {};
                return (
                  <button key={name} onClick={() => nav("/riders")} data-testid={`weekly-following-${name}`}
                    className={`rounded-xl p-3 flex items-center justify-between gap-2 text-left ${inner} hover:ring-1 hover:ring-[#C8000F]/40 transition`}>
                    <span className="font-bold text-[14px] truncate">{name}</span>
                    <span className="shrink-0 text-right">
                      <span className="block text-sm font-bold text-[#16A34A]">{money(t.total)}</span>
                      {t.cfr > 0 && <span className={`block text-[10px] ${sub}`}>S {money(t.season)} · CFR {money(t.cfr)}</span>}
                    </span>
                  </button>
                );
              })}
            </div>}
        </section>

        {/* DEEP RESEARCH RECAP */}
        <section className={`rounded-2xl p-4 lg:p-5 mb-5 ${card}`} data-testid="weekly-deep">
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <div className="flex items-center gap-2"><Microscope className="w-4 h-4 text-[#C8000F]" /><h2 className="font-display font-bold text-lg">Deep Research Recap</h2></div>
            <button onClick={generateDeep} disabled={deepLoading} data-testid="weekly-deep-generate"
              className={`text-xs font-bold rounded-full px-3 py-1.5 flex items-center gap-1.5 transition-colors ${deepLoading ? "bg-[#ECEEF1] text-slate-400" : "bg-[#C8000F] text-white hover:bg-[#a4000c]"}`}>
              {deepLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Researching…</> : <><RefreshCw className="w-3.5 h-3.5" /> {deep ? "Regenerate" : "Generate"}</>}
            </button>
          </div>
          <p className={`text-xs ${sub} mb-3`}>AI researches the web (all circuits) for verified results on the riders you follow, cites its sources, and flags standout runs — never guesses.</p>

          {/* research roster editor */}
          <div className={`rounded-xl p-3 mb-3 ${inner}`} data-testid="weekly-roster">
            <div className="flex items-center gap-1.5 mb-2"><Users className="w-3.5 h-3.5 text-[#C8000F]" /><span className="font-bold text-[13px]">Research roster</span>
              <span className={`text-[11px] ${sub}`}>— add any rider (even PRCA/US names not in our Canadian data)</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(roster.length === 0 && myFollowing.length === 0)
                ? <span className={`text-[12px] ${sub}`}>No riders yet — add names below, or follow riders above.</span>
                : <>
                  {myFollowing.map((n) => (
                    <span key={`f-${n}`} className={`text-[11px] rounded-full px-2.5 py-1 flex items-center gap-1 ${m ? "bg-white/5 text-slate-300" : "bg-[#ECEEF1] text-slate-600"}`} data-testid={`weekly-roster-follow-${n}`}>
                      <Check className="w-3 h-3 text-[#16A34A]" /> {n}
                    </span>
                  ))}
                  {roster.map((n) => (
                    <span key={`r-${n}`} className="text-[11px] rounded-full px-2.5 py-1 flex items-center gap-1 bg-[#C8000F]/10 text-[#C8000F] font-semibold" data-testid={`weekly-roster-name-${n}`}>
                      {n}
                      <button onClick={() => removeRosterName(n)} data-testid={`weekly-roster-remove-${n}`} className="hover:opacity-70"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </>}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="weekly-roster-input"
                value={rosterInput}
                onChange={(e) => setRosterInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRosterName(); } }}
                placeholder="Add a rider — e.g. Shad Mayfield, Riley Webb…"
                className={`flex-1 rounded-lg border px-3 py-1.5 text-[13px] outline-none focus:border-[#C8000F] transition-colors ${inputCls}`}
              />
              <button onClick={addRosterName} data-testid="weekly-roster-add" className="shrink-0 text-xs font-bold rounded-lg px-3 py-1.5 bg-[#C8000F] text-white hover:bg-[#a4000c] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
          </div>
          {deepLoading && <div className={`rounded-xl p-4 text-sm ${inner} ${sub} flex items-center gap-2`}><Loader2 className="w-4 h-4 animate-spin text-[#C8000F]" /> Searching official results and credible sources… this usually takes 20–40 seconds.</div>}
          {deepErr && !deepLoading && <p className="text-sm text-[#C8000F]" data-testid="weekly-deep-error">{deepErr}</p>}
          {!deepLoading && deep === undefined && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>}
          {!deepLoading && deep === null && !deepErr && <p className={`text-sm ${sub}`}>No recap yet — tap <strong>Generate</strong> to research this week's verified results for the riders you follow.</p>}
          {!deepLoading && deep && (
            <div data-testid="weekly-deep-content">
              <div className={`text-[11px] ${sub} mb-2`}>Window {deep.window?.start} → {deep.window?.end} · generated {new Date(deep.generated_at).toLocaleString()}{deep.flagged_saved > 0 ? ` · ${deep.flagged_saved} new run${deep.flagged_saved === 1 ? "" : "s"} flagged` : ""}</div>
              <div className={`rounded-xl p-3.5 ${inner}`}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{deep.recap_markdown}</ReactMarkdown>
              </div>
              {(deep.citations || []).length > 0 && (
                <div className="mt-3">
                  <div className={`text-[11px] font-bold uppercase tracking-wide ${sub} mb-1.5`}>Sources</div>
                  <div className="flex flex-wrap gap-2">
                    {deep.citations.map((c, k) => (
                      <a key={k} href={c.url} target="_blank" rel="noreferrer" data-testid={`weekly-deep-cite-${k}`}
                        className={`text-[11px] rounded-full px-2.5 py-1 flex items-center gap-1 ${m ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-[#ECEEF1] text-slate-600 hover:bg-[#E0E3E7]"}`}>
                        <ExternalLink className="w-3 h-3" /> {(c.title || c.url).slice(0, 48)}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* FLAGGED RUNS DATASET */}
        <section className={`rounded-2xl p-4 lg:p-5 mb-5 ${card}`} data-testid="weekly-flagged">
          <div className="flex items-center gap-2 mb-1"><Bookmark className="w-4 h-4 text-[#C8000F]" /><h2 className="font-display font-bold text-lg">Flagged Runs Database</h2></div>
          <p className={`text-xs ${sub} mb-3`}>Standout runs the research flagged as worth tracking — your growing segmentation dataset. Fields stay "unknown" when not reliably sourced.</p>
          {flagged.length === 0 ? <p className={`text-sm ${sub}`}>No flagged runs yet. Generate a Deep Research Recap above to start building your dataset.</p>
            : <div className="space-y-2">
              {flagged.map((r) => (
                <div key={r.id} className={`rounded-xl p-3 ${inner}`} data-testid={`weekly-flagged-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-bold text-[14px] flex items-center gap-1.5 flex-wrap">
                        {r.contestant}
                        {r.run_time && r.run_time !== "unknown" && <span className="text-[#C8000F] font-extrabold">{r.run_time}s</span>}
                        {r.confirmed ? <span className="text-[9px] font-bold uppercase bg-[#16A34A]/15 text-[#16A34A] rounded px-1.5 py-0.5">confirmed</span>
                          : <span className={`text-[9px] font-bold uppercase ${m ? "bg-white/10 text-slate-400" : "bg-[#ECEEF1] text-slate-500"} rounded px-1.5 py-0.5`}>unverified</span>}
                      </div>
                      <div className={`text-[12.5px] ${sub} mt-0.5`}>
                        {[r.event, r.rodeo, r.location, r.date].filter((x) => x && x !== "unknown").join(" · ")}
                      </div>
                      <div className={`text-[12px] ${sub} mt-0.5 flex flex-wrap gap-x-3`}>
                        <span>Place: <b className={r.placing && r.placing !== "unknown" ? "" : "opacity-60"}>{r.placing || "unknown"}</b></span>
                        <span>Wraps: <b className={r.wraps !== "unknown" ? "" : "opacity-60"}>{r.wraps}</b></span>
                        <span>Calf: <b className={r.calf_result !== "unknown" ? "" : "opacity-60"}>{r.calf_result}</b></span>
                      </div>
                      {r.standout_reason && <div className={`text-[12px] italic ${sub} mt-1`}>{r.standout_reason}</div>}
                      {r.source_url && r.source_url !== "unknown" && <a href={r.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-[#C8000F] underline inline-flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" /> source</a>}
                    </div>
                    <button onClick={() => deleteFlagged(r.id)} data-testid={`weekly-flagged-delete-${r.id}`} className={`shrink-0 ${sub} hover:text-[#C8000F] p-1`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>}
        </section>

        {/* RECAP */}
        <section className={`rounded-2xl p-4 lg:p-5 mb-5 ${card}`} data-testid="weekly-recap">
          <div className="flex items-center gap-2 mb-3"><CalendarDays className="w-4 h-4 text-[#C8000F]" /><h2 className="font-display font-bold text-lg">Your Weekly Recap</h2></div>
          {!recap ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
            : recap.no_results ? <p className={`text-sm ${sub}`}>No verified results for the riders you follow in this window. Try a longer range above.</p>
              : <>
                {recap.summary && <p className="text-[15px] leading-relaxed mb-4 border-l-[3px] border-[#C8000F] pl-3" data-testid="weekly-recap-summary">{recap.summary}</p>}
                <div className="space-y-2.5">
                  {recap.riders.map((r) => (
                    <div key={r.name} className={`rounded-xl p-3 ${inner}`} data-testid={`weekly-rider-${r.name}`}>
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <button onClick={() => nav("/riders")} className="font-bold text-[15px] hover:text-[#C8000F] flex items-center gap-1.5">
                          {r.name}{r.self && <span className="text-[9px] font-bold uppercase bg-[#C8000F] text-white rounded-full px-1.5 py-0.5">You</span>}
                        </button>
                        <span className="text-sm font-bold text-[#16A34A]">{money(r.won)} · {r.placings} placing{r.placings === 1 ? "" : "s"}</span>
                      </div>
                      {r.results.map((i, k) => (
                        <div key={k} className={`flex items-center justify-between gap-2 text-[12.5px] ${sub} py-0.5`}>
                          <span className="truncate">{i.place || "NP"} · {i.rodeo}{i.cfr && <CfrBadge />} <span className="opacity-70">({i.event}{i.value ? `, ${i.value}` : ""})</span></span>
                          {i.earnings > 0 && <span className="shrink-0 font-semibold text-[#16A34A]">{money(i.earnings)}</span>}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>}
        </section>

        {/* WHO BEAT WHO */}
        <section className={`rounded-2xl p-4 lg:p-5 mb-5 ${card}`} data-testid="weekly-h2h">
          <div className="flex items-center gap-2 mb-1"><Swords className="w-4 h-4 text-[#C8000F]" /><h2 className="font-display font-bold text-lg">Who Beat Who</h2></div>
          <p className={`text-xs ${sub} mb-3`}>Head-to-head results for the riders you follow — including the CFR.</p>
          {!h2h ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
            : h2h.empty ? <p className={`text-sm ${sub}`}>No head-to-head matchups for your riders in this window. Follow more riders or widen the range.</p>
              : <div className="space-y-2.5">
                {h2h.matchups.map((mu, k) => (
                  <div key={k} className={`rounded-xl p-3 ${inner}`} data-testid={`weekly-h2h-${k}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                      <span className="font-bold text-[14px] flex items-center gap-1.5">{mu.rider}
                        <span className={`text-[11px] font-semibold ${sub}`}>· {mu.place ? `${mu.place}` : "NP"} of {mu.field}</span>
                      </span>
                      <span className={`text-[11px] ${sub} flex items-center`}>{mu.rodeo}{mu.cfr && <CfrBadge />} · {mu.event}</span>
                    </div>
                    {mu.beat.length > 0 && <div className="text-[12.5px]"><span className="text-[#16A34A] font-semibold">Beat:</span> <span className={sub}>{mu.beat.join(", ")}</span></div>}
                    {mu.lost_to.length > 0 && <div className="text-[12.5px]"><span className="text-[#C8000F] font-semibold">Lost to:</span> <span className={sub}>{mu.lost_to.join(", ")}</span></div>}
                  </div>
                ))}
              </div>}
        </section>

        {/* RIVALRIES TO WATCH (season-long) */}
        <section className={`rounded-2xl p-4 lg:p-5 mb-5 ${card}`} data-testid="weekly-rivalries">
          <div className="flex items-center gap-2 mb-1"><Flame className="w-4 h-4 text-[#C8000F]" /><h2 className="font-display font-bold text-lg">Rivalries to Watch</h2></div>
          <p className={`text-xs ${sub} mb-3`}>Each rider you follow against their nearest rival in the season standings — plus their head-to-head record this year.</p>
          {!riv ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
            : riv.empty ? <p className={`text-sm ${sub}`}>Follow a few riders who are ranked in the season standings and we'll track their rivalries here.</p>
              : <div className="space-y-2.5">
                {riv.rivalries.map((r, k) => {
                  const total = Math.max(r.rider_wins + r.rival_wins, 1);
                  const pct = Math.round((r.rider_wins / total) * 100);
                  return (
                  <div key={k} className={`rounded-xl p-3 ${inner}`} data-testid={`weekly-rivalry-${k}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-[#C8000F]">{r.event}</span>
                      <span className={`text-[11px] ${sub}`}>{money(r.gap)} apart</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-[14px]">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold truncate">{r.rider}</div>
                        <div className={`text-[11px] ${sub}`}>#{r.rider_rank} · {money(r.rider_total)}</div>
                      </div>
                      <div className="shrink-0 text-center px-2">
                        <div className="font-display font-extrabold text-[15px]">{r.rider_wins}<span className={sub}>–</span>{r.rival_wins}</div>
                        <div className={`text-[10px] ${sub}`}>{r.meetings} met</div>
                      </div>
                      <div className="min-w-0 flex-1 text-right">
                        <div className="font-bold truncate">{r.rival}</div>
                        <div className={`text-[11px] ${sub}`}>#{r.rival_rank} · {money(r.rival_total)}</div>
                        {!isFollowing(r.rival) && <div className="mt-1.5 flex justify-end"><FollowBtn name={r.rival} /></div>}
                      </div>
                    </div>
                    {(r.rider_wins + r.rival_wins) > 0 && (
                      <div className="mt-2 h-1.5 rounded-full overflow-hidden bg-[#C8000F]/15 flex">
                        <div className="h-full bg-[#16A34A]" style={{ width: `${pct}%` }} />
                        <div className="h-full bg-[#C8000F]" style={{ width: `${100 - pct}%` }} />
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>}
        </section>

        {/* RECOMMENDED CONTESTANTS */}
        <section className={`rounded-2xl p-4 lg:p-5 mb-5 ${card}`} data-testid="weekly-recommended">
          <div className="flex items-center gap-2 mb-1"><Compass className="w-4 h-4 text-[#C8000F]" /><h2 className="font-display font-bold text-lg">Recommended to Follow</h2></div>
          <p className={`text-xs ${sub} mb-3`}>The leaders in every event — ranked by season + CFR money — that you don't follow yet.</p>
          {!rec ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
            : rec.empty ? <p className={`text-sm ${sub}`}>No new recommendations right now — you may already follow the top contestants in every event.</p>
              : <>
                {rec.ai && <p className="text-[14px] leading-relaxed mb-3 border-l-[3px] border-[#C8000F] pl-3" data-testid="weekly-recommended-ai">{rec.ai}</p>}
                <div className="space-y-4">
                  {rec.groups.map((g) => (
                    <div key={g.event} data-testid={`weekly-rec-event-${g.event}`}>
                      <div className="font-bold text-[13px] uppercase tracking-wide text-[#C8000F] mb-1.5">{g.event}</div>
                      <div className="space-y-2">
                        {g.contestants.map((c, k) => (
                          <div key={k} className={`rounded-xl p-3 flex items-center justify-between gap-3 ${inner}`} data-testid={`weekly-rec-${g.event}-${k}`}>
                            <div className="min-w-0">
                              <div className="font-bold text-[14px] truncate flex items-center">{c.name}{c.cfr > 0 && <CfrBadge />}</div>
                              <div className={`text-[12px] ${sub} truncate`}>{c.reason}</div>
                            </div>
                            <FollowBtn name={c.name} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>}
        </section>

        {/* HOT THIS WEEK */}
        <section className={`rounded-2xl p-4 lg:p-5 mb-5 ${card}`} data-testid="weekly-hot">
          <div className="flex items-center gap-2 mb-1"><Flame className="w-4 h-4 text-[#C8000F]" /><h2 className="font-display font-bold text-lg">Hot This Week</h2></div>
          <p className={`text-xs ${sub} mb-3`}>The biggest recent earners you don't follow — regular season and the CFR.</p>
          {!hot ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
            : hot.empty ? <p className={`text-sm ${sub}`}>No breakout performances recorded in this window yet.</p>
              : <div className="space-y-2">
                {hot.rising.map((c, k) => (
                  <div key={k} className={`rounded-xl p-3 flex items-center justify-between gap-3 ${inner}`} data-testid={`weekly-hot-${k}`}>
                    <div className="min-w-0">
                      <div className="font-bold text-[14px] truncate flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-[#E0A800] shrink-0" />{c.name}{c.cfr_won > 0 && <CfrBadge />}</div>
                      <div className={`text-[12px] ${sub} truncate`}>{money(c.recent_won)}{c.rodeos > 0 ? ` · ${c.rodeos} rodeo${c.rodeos === 1 ? "" : "s"}` : ""}{c.event ? ` · ${c.event}` : ""}{c.season_rank ? ` · #${c.season_rank} season` : ""}</div>
                    </div>
                    <FollowBtn name={c.name} />
                  </div>
                ))}
              </div>}
        </section>

        {/* THIS WEEK IN RODEO */}
        <section className={`rounded-2xl p-4 lg:p-5 mb-5 ${card}`} data-testid="weekly-highlights">
          <div className="flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-[#C8000F]" /><h2 className="font-display font-bold text-lg">This Week in Rodeo</h2></div>
          {!hi ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
            : hi.empty ? <p className={`text-sm ${sub}`}>No verified winners recorded in this window yet.</p>
              : <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(hi.events).sort().map(([ev, rows]) => (
                  <div key={ev} className={`rounded-xl p-3 ${inner}`} data-testid={`weekly-event-${ev}`}>
                    <div className="font-bold text-[13px] uppercase tracking-wide text-[#C8000F] mb-1.5">{ev}</div>
                    {rows.map((w, k) => (
                      <div key={k} className="flex items-center justify-between gap-2 text-[12.5px] py-0.5">
                        <span className="truncate">{k === 0 && <Trophy className="w-3 h-3 inline mr-1 text-[#E0A800]" />}{w.name} <span className={sub}>· {w.rodeo}</span></span>
                        {w.earnings > 0 && <span className="shrink-0 font-semibold text-[#16A34A]">{money(w.earnings)}</span>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>}
        </section>

        {/* BIGGEST MOVERS */}
        <section className={`rounded-2xl p-4 lg:p-5 ${card}`} data-testid="weekly-movers">
          <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-4 h-4 text-[#C8000F]" /><h2 className="font-display font-bold text-lg">Biggest Movers</h2></div>
          <p className={`text-xs ${sub} mb-3`}>PRCA world-standings earnings gains over the last week.</p>
          {!movers ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
            : !movers.ready ? <p className={`text-sm ${sub}`}>Building standings history — climbers appear once we have two snapshots (within a day or two).</p>
              : (movers.climbers || []).length === 0 ? <p className={`text-sm ${sub}`}>No standings movement recorded in the latest window.</p>
                : <div className="space-y-1.5">
                  {(movers.climbers || []).slice(0, 15).map((c, k) => (
                    <div key={k} className="flex items-center justify-between gap-2 text-[13px] py-1 border-b last:border-0 border-white/5" data-testid={`weekly-mover-${k}`}>
                      <span className="truncate flex items-center gap-1.5"><ArrowUpRight className="w-3.5 h-3.5 text-[#16A34A]" />{c.name} <span className={sub}>· {c.event || c.abbr}</span></span>
                      <span className="shrink-0 font-semibold text-[#16A34A]">{c.delta > 0 ? "+" : ""}{money(c.delta)}</span>
                    </div>
                  ))}
                </div>}
        </section>
      </div>
    </div>
  );
}
